import { useState, useEffect, useRef, useCallback } from "react";
import { X, Loader2 } from "lucide-react";
import { getUserUuid, isUserInArray } from "../utils/auth";
import { getDocIdData } from "../../../../api/universal";
import { useRightSidebar } from "../../../contexts/RightSidebarContext";
import { useArticleSidebar } from "../../../Universal/ArticleCardDetailPanel";

/**
 * Manages citation interactions and source sidebar opening.
 *
 * @param {Array} messages - Current chat messages (to derive initial interactions)
 */
const useSourceSidebar = (messages) => {
  const {
    isRightOpen,
    setIsRightOpen,
    setRightComponent,
    setRightSidebarMode,
    rightSidebarMode,
  } = useRightSidebar();

  /** Track fetched source articles as array of { id, article } */
  const [sourceArticles, setSourceArticles] = useState([]);

  /**
   * Track per-source like/dislike/catalog as { [doc_id]: { like, dislike, catalog } }.
   */
  const [citationInteractions, setCitationInteractions] = useState({});
  const citationInteractionsRef = useRef(citationInteractions);
  useEffect(() => {
    citationInteractionsRef.current = citationInteractions;
  }, [citationInteractions]);

  // Derive initial interactions from message citations whenever messages change
  const userUuidRef = useRef(getUserUuid());
  useEffect(() => {
    const uid = userUuidRef.current;
    setCitationInteractions((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const msg of messages) {
        if (msg.type !== "answer" || !msg.citations) continue;
        for (const c of msg.citations) {
          if (!c.doc_id || next[c.doc_id]) continue;
          const likeArr = Array.isArray(c.like) ? c.like : [];
          const dislikeArr = Array.isArray(c.dislike) ? c.dislike : [];
          const catalogArr = Array.isArray(c.catalog) ? c.catalog : [];
          if (likeArr.length || dislikeArr.length || catalogArr.length) {
            next[c.doc_id] = {
              like: isUserInArray(likeArr, uid),
              dislike: isUserInArray(dislikeArr, uid),
              catalog: isUserInArray(catalogArr, uid),
            };
            changed = true;
          }
        }
      }
      return changed ? next : prev;
    });
  }, [messages]);

  /** Called by ArticleDetailPanel when user interacts with like/dislike/catalog in sidebar */
  const handleSourceInteract = useCallback(
    (cardId, docId, { like, dislike, catalog }) => {
      setCitationInteractions((prev) => ({
        ...prev,
        [docId]: { like, dislike, catalog },
      }));
      setSourceArticles((prev) =>
        prev.map((e) =>
          e.id === docId
            ? { ...e, article: { ...e.article, like, dislike, catalog } }
            : e,
        ),
      );
    },
    [],
  );

  const {
    activeCardId,
    setActiveCardId,
    selectedCardId,
    setSelectedCardId,
    handleCardClick: openArticleSidebar,
  } = useArticleSidebar(sourceArticles, handleSourceInteract);

  // Clear active source highlight when the right sidebar closes
  useEffect(() => {
    if (!isRightOpen) {
      setActiveCardId(null);
    }
  }, [isRightOpen, setActiveCardId]);

  const handleSourceClickRef = useRef(null);

  /** Shared handler for citation + related-source clicks */
  const handleSourceClick = useCallback(
    (source) => {
      const docId = source.doc_id;
      if (!docId) return;

      // Skip if this source is already open in the sidebar
      if (activeCardId === docId && isRightOpen) return;

      // Always update the highlighted source immediately
      setActiveCardId(docId);

      // If we already fetched this doc, just open it
      const existing = sourceArticles.find((e) => e.id === docId);
      if (existing) {
        openArticleSidebar(docId, existing.article);
        return;
      }

      // Show loading state in sidebar
      setSelectedCardId(null);
      setIsRightOpen(true);
      setRightSidebarMode(null);
      setRightComponent(
        <div className="p-4 text-[13px] text-zinc-500 dark:text-zinc-400 flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading source…
        </div>,
      );

      getDocIdData(docId)
        .then((response) => {
          const article = response?.data;
          if (!article) throw new Error("No data returned");

          const uid = userUuidRef.current;
          const existingInteraction = citationInteractionsRef.current[docId];
          const interactionState = existingInteraction || {
            like:
              typeof article.like === "boolean"
                ? article.like
                : isUserInArray(article.like, uid),
            dislike:
              typeof article.dislike === "boolean"
                ? article.dislike
                : isUserInArray(article.dislike, uid),
            catalog:
              typeof article.catalog === "boolean"
                ? article.catalog
                : isUserInArray(article.catalog, uid),
          };

          const enriched = {
            ...article,
            like: interactionState.like,
            dislike: interactionState.dislike,
            catalog: interactionState.catalog,
          };

          if (!existingInteraction) {
            setCitationInteractions((prev) => ({
              ...prev,
              [docId]: interactionState,
            }));
          }

          setSourceArticles((prev) => [
            ...prev,
            { id: docId, article: enriched },
          ]);

          requestAnimationFrame(() => {
            openArticleSidebar(docId, enriched);
          });
        })
        .catch((err) => {
          console.error("getDocIdData error:", err);
          setSelectedCardId(null);
          setRightComponent(
            <div className="flex flex-col items-center justify-center gap-3 p-6 text-center h-full">
              <div className="rounded-full bg-red-100 dark:bg-red-900/30 p-3">
                <X className="h-5 w-5 text-red-500 dark:text-red-400" />
              </div>
              <p className="text-[13px] font-medium text-zinc-800 dark:text-zinc-200">
                Unable to load source
              </p>
              <p className="text-[12px] text-zinc-500 dark:text-zinc-400 max-w-[220px]">
                {source.title
                  ? `"${source.title.length > 60 ? source.title.slice(0, 59) + "…" : source.title}"`
                  : "This document"}{" "}
                could not be retrieved. The server returned an error.
              </p>
              <button
                onClick={() => handleSourceClickRef.current?.(source)}
                className="mt-1 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] font-medium bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300 hover:bg-zinc-200 dark:hover:bg-zinc-700 transition-colors"
              >
                <Loader2 className="h-3 w-3" style={{ animation: "none" }} />
                Retry
              </button>
              <button
                onClick={() => setIsRightOpen(false)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] font-medium text-zinc-500 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
              >
                Close
              </button>
            </div>,
          );
        });
    },
    [
      sourceArticles,
      openArticleSidebar,
      setActiveCardId,
      setSelectedCardId,
      activeCardId,
      isRightOpen,
      setIsRightOpen,
      setRightSidebarMode,
      setRightComponent,
      setCitationInteractions,
    ],
  );
  handleSourceClickRef.current = handleSourceClick;

  return {
    activeCardId,
    citationInteractions,
    handleSourceClick,
    handleSourceInteract,
    isRightOpen,
  };
};

export default useSourceSidebar;
